import type { DeliveryNoteInternal } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchDeliveryNoteInternal } from './fetchDeliveryNoteInternal';

/**
 * Generates the query options for the internal delivery note query so they can be shared between
 * the useQuery hook and prefetching.
 *
 * @param assetId - The asset ID for the delivery note to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export function getDeliveryNoteInternalQueryOptions<
  TData = DeliveryNoteInternal,
>({
  assetId,
  options,
}: {
  assetId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<DeliveryNoteInternal, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) {
  return queryOptions({
    queryFn: assetId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchDeliveryNoteInternal(assetId, { signal })
      : skipToken,
    queryKey: queryKeysDeliveryNote.getInternal(assetId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });
}

/**
 * React Query based custom hook for getting internal delivery note data for a given asset ID.
 *
 * @param assetId - The asset ID for the delivery note to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_delivery_note_internal_delivery_note__asset_id__internal_get
 */
export function useQueryDeliveryNoteInternal<TData = DeliveryNoteInternal>(
  assetId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<DeliveryNoteInternal, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery(
    getDeliveryNoteInternalQueryOptions<TData>({ assetId, options }),
  );
}
